import React, { useRef } from 'react';
import { Task } from '@/context/TaskContext';
import { TaskCardCompact } from './tasks/TaskCardCompact';
import { TaskCardFull } from './tasks/TaskCardFull';
import { useTaskDetailModal } from './tasks/TaskDetailModalContext';
import { useIsMobile } from '@/hooks/use-mobile';

interface TaskCardProps {
  task: Task;
  onSelect: (id: string) => void;
  isActive: boolean;
  compact?: boolean;
}

// Touch/long-press for mobile detail modal
function useDetailGesture(task: Task) {
  const { open } = useTaskDetailModal();
  const isMobile = useIsMobile();

  // For tracking long-press
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const handlers = isMobile
    ? {
        onTouchStart: () => {
          timeout.current = setTimeout(() => open(task), 500);
        },
        onTouchEnd: () => {
          if (timeout.current) clearTimeout(timeout.current);
        },
        onTouchMove: () => {
          if (timeout.current) clearTimeout(timeout.current);
        }
      }
    : {
        onDoubleClick: (e: React.MouseEvent) => {
          // Prevent selection click if doubleclick triggers
          e.stopPropagation();
          open(task);
        }
      };

  return handlers;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  onSelect, 
  isActive, 
  compact = false 
}) => {
  const detailHandlers = useDetailGesture(task);

  if (compact) {
    return (
      <div {...detailHandlers}>
        <TaskCardCompact 
          task={task} 
          onSelect={onSelect} 
          isActive={isActive} 
        />
      </div>
    );
  }

  return (
    <div {...detailHandlers}>
      <TaskCardFull 
        task={task} 
        onSelect={onSelect} 
        isActive={isActive} 
      />
    </div>
  );
};

export default TaskCard;
